import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { ShoppingBag, Menu, X, User } from 'lucide-react';
import Logo from './Logo';
import { useCart } from '../context/CartContext';
import { getSettings } from '../lib/api';
import BlackFridayStrip from './BlackFridayStrip';

const NAV = [
  { to: '/', label: 'Home' },
  { to: '/shop', label: 'Shop' },
  { to: '/calculator', label: 'Calculator' },
  { to: '/reviews', label: 'Reviews' },
  { to: '/shipping', label: 'Shipping' },
  { to: '/faq', label: 'FAQ' },
  { to: '/contact', label: 'Contact' },
];

export default function Header() {
  const { cartCount, openDrawer } = useCart();
  const [open, setOpen] = useState(false);
  const [announcement, setAnnouncement] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    getSettings().then((s) => setAnnouncement(s?.announcement || null)).catch(() => {});
  }, []);

  return (
    <header className="sticky top-0 z-50 bg-alp-black/70 backdrop-blur-xl border-b border-alp-gold/15 shadow-[0_4px_30px_rgba(0,0,0,0.6)]">
      <BlackFridayStrip />
      {announcement && (
        <div data-testid="announcement-bar" className="announcement-bar-wrap text-xs md:text-sm tracking-[0.05em] py-2 overflow-hidden" style={{ backgroundColor: announcement.bg || '#C9A227', color: announcement.color || '#0A0A0A' }}>
          <div className="announcement-marquee" style={{ animationDuration: `${60 / (announcement.speed || 2)}s` }}>
            {[0, 1].map((g) => (
              <div key={g} className="flex items-center shrink-0" aria-hidden={g === 1}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <span key={i} className="flex items-center gap-3 px-8 whitespace-nowrap">
                    <span className="font-medium">{announcement.text}</span>
                    {announcement.buttonText && announcement.buttonLink && (
                      <a
                        href={announcement.buttonLink}
                        data-testid={g === 0 && i === 0 ? 'announcement-bar-btn' : undefined}
                        className="underline font-semibold whitespace-nowrap hover:opacity-80"
                      >
                        {announcement.buttonText}
                      </a>
                    )}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group">
          <Logo size={46} />
          <div className="hidden sm:block leading-tight">
            <div className="font-serif text-alp-cream text-lg tracking-[0.2em] uppercase">
              Australian Luxe
            </div>
            <div className="text-alp-gold text-xs tracking-[0.35em] uppercase">
              Peptides
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `nav-underline text-[11px] tracking-[0.18em] uppercase transition-colors duration-300 ${
                  isActive ? 'text-alp-gold nav-active' : 'text-alp-cream/80 hover:text-alp-gold'
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/account')}
            data-testid="header-account-btn"
            className="p-2 text-alp-cream hover:text-alp-gold transition-colors"
            aria-label="Account"
          >
            <User size={22} strokeWidth={1.5} />
          </button>
          <button
            onClick={openDrawer}
            data-testid="header-cart-btn"
            className="relative p-2 text-alp-cream hover:text-alp-gold transition-colors"
            aria-label="Cart"
          >
            <ShoppingBag size={22} strokeWidth={1.5} />
            {cartCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-alp-gold text-alp-black text-[10px] font-semibold w-4.5 h-4.5 min-w-[18px] h-[18px] rounded-full flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </button>
          <button
            className="md:hidden p-2 text-alp-cream"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="md:hidden border-t border-alp-line bg-alp-black px-5 py-4 flex flex-col gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `py-3 text-sm tracking-[0.2em] uppercase border-b border-alp-line/50 ${
                  isActive ? 'text-alp-gold' : 'text-alp-cream/80'
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <NavLink
            to="/account"
            onClick={() => setOpen(false)}
            data-testid="mobile-account-link"
            className={({ isActive }) =>
              `py-3 text-sm tracking-[0.2em] uppercase border-b border-alp-line/50 ${
                isActive ? 'text-alp-gold' : 'text-alp-cream/80'
              }`
            }
          >
            Sign In / Account
          </NavLink>
        </nav>
      )}
    </header>
  );
}
